import { useRef, useEffect, useCallback } from 'react'
import { Search, X } from 'lucide-react'

interface SearchBarProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
}

export default function SearchBar({ value, onChange, disabled, placeholder }: SearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  // Keyboard shortcut: Ctrl/Cmd + K to focus
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        inputRef.current?.select()
      }
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        onChange('')
        inputRef.current?.blur()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onChange])

  // Auto-focus on mount
  useEffect(() => {
    if (!disabled) {
      inputRef.current?.focus()
    }
  }, [disabled])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }, [onChange])

  return (
    <div className="relative max-w-2xl mx-auto">
      {/* Search icon */}
      <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
        <Search size={20} className="text-surface-500" />
      </div>

      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full h-12 pl-12 pr-24 bg-surface-800/80 border border-surface-700/50 rounded-xl text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-accent-500/50 focus:border-accent-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        autoComplete="off"
        spellCheck={false}
      />

      {/* Keyboard hint */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-1 text-surface-600 text-sm">
        <kbd className="px-1.5 py-0.5 bg-surface-700/50 border border-surface-600/50 rounded text-xs font-mono">⌘K</kbd>
      </div>

      {/* Clear button when there's text */}
      {value && (
        <button
          onClick={() => onChange('')}
          className="absolute right-14 sm:right-16 top-1/2 -translate-y-1/2 p-1.5 text-surface-500 hover:text-surface-300 transition-colors rounded-lg hover:bg-surface-700/50"
        >
          <X size={18} />
        </button>
      )}
    </div>
  )
}
